/**
 * Utility functions for calculating map bounds from search results and favorites
 */

/**
 * Extract coordinates from a unit/parcel object
 * @param {Object} unit - Unit object with coordinates property
 * @returns {Array|null} - [lng, lat] or null if invalid
 */
export const extractUnitCoordinates = (unit) => {
  if (!unit) return null;

  // Use parent project coordinates — units don't carry their own lat/lng,
  // and zooming on the project is what we actually want. The project
  // can be nested under `project`, `block.project`, or `floor.project`,
  // and any of them might be present-but-empty, so check all three.
  const candidates = [unit.project, unit.block?.project, unit.floor?.project];
  for (const project of candidates) {
    if (project && project.latitude != null && project.longitude != null) {
      const lng = parseFloat(project.longitude);
      const lat = parseFloat(project.latitude);
      if (!isNaN(lng) && !isNaN(lat)) {
        return [lng, lat];
      }
    }
  }

  return null;
};

/**
 * Extract coordinates from a project object
 * @param {Object} project - Project object with latitude/longitude properties
 * @returns {Array|null} - [lng, lat] or null if invalid
 */
export const extractProjectCoordinates = (project) => {
  if (!project) return null;

  // Projects have latitude and longitude properties
  if (project.latitude && project.longitude) {
    return [parseFloat(project.longitude), parseFloat(project.latitude)];
  }

  return null;
};

/**
 * Calculate bounds from an array of units
 * @param {Array} units - Array of unit objects
 * @returns {Object|null} - Bounds object {minLng, minLat, maxLng, maxLat} or null if no valid coordinates
 */
export const calculateUnitsBounds = (units) => {
  if (!Array.isArray(units) || units.length === 0) return null;

  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  let hasValidCoordinates = false;

  units.forEach(unit => {
    const coords = extractUnitCoordinates(unit);
    if (coords && coords.length >= 2) {
      const [lng, lat] = coords;
      if (!isNaN(lng) && !isNaN(lat)) {
        minLng = Math.min(minLng, lng);
        minLat = Math.min(minLat, lat);
        maxLng = Math.max(maxLng, lng);
        maxLat = Math.max(maxLat, lat);
        hasValidCoordinates = true;
      }
    }
  });

  if (!hasValidCoordinates) return null;

  return {
    minLng,
    minLat,
    maxLng,
    maxLat
  };
};

/**
 * Calculate bounds from an array of projects
 * @param {Array} projects - Array of project objects
 * @returns {Object|null} - Bounds object {minLng, minLat, maxLng, maxLat} or null if no valid coordinates
 */
export const calculateProjectsBounds = (projects) => {
  if (!Array.isArray(projects) || projects.length === 0) return null;

  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  let hasValidCoordinates = false;

  projects.forEach(project => {
    const coords = extractProjectCoordinates(project);
    if (coords && coords.length >= 2) {
      const [lng, lat] = coords;
      if (!isNaN(lng) && !isNaN(lat)) {
        minLng = Math.min(minLng, lng);
        minLat = Math.min(minLat, lat);
        maxLng = Math.max(maxLng, lng);
        maxLat = Math.max(maxLat, lat);
        hasValidCoordinates = true;
      }
    }
  });

  if (!hasValidCoordinates) return null;

  return {
    minLng,
    minLat,
    maxLng,
    maxLat
  };
};

/**
 * Calculate combined bounds from both units and projects
 * @param {Array} units - Array of unit objects
 * @param {Array} projects - Array of project objects
 * @returns {Object|null} - Bounds object {minLng, minLat, maxLng, maxLat} or null if no valid coordinates
 */
export const calculateCombinedBounds = (units, projects) => {
  const unitsBounds = calculateUnitsBounds(units);
  const projectsBounds = calculateProjectsBounds(projects);

  if (!unitsBounds && !projectsBounds) return null;
  if (!unitsBounds) return projectsBounds;
  if (!projectsBounds) return unitsBounds;

  // Combine both bounds
  return {
    minLng: Math.min(unitsBounds.minLng, projectsBounds.minLng),
    minLat: Math.min(unitsBounds.minLat, projectsBounds.minLat),
    maxLng: Math.max(unitsBounds.maxLng, projectsBounds.maxLng),
    maxLat: Math.max(unitsBounds.maxLat, projectsBounds.maxLat)
  };
};

/**
 * Convert bounds object to Mapbox LngLatBounds format
 * @param {Object} bounds - Bounds object {minLng, minLat, maxLng, maxLat}
 * @returns {Array} - [[minLng, minLat], [maxLng, maxLat]]
 */
export const boundsToMapboxFormat = (bounds) => {
  if (!bounds) return null;
  return [[bounds.minLng, bounds.minLat], [bounds.maxLng, bounds.maxLat]];
};

/**
 * Fit map to bounds with proper options
 * @param {Object} mapRef - Mapbox map reference
 * @param {Object} bounds - Bounds object {minLng, minLat, maxLng, maxLat}
 * @param {Object} options - Additional fitBounds options
 */
export const fitMapToBounds = (mapRef, bounds, options = {}) => {
  if (!mapRef || !mapRef.current || !bounds) return;

  try {
    const mapboxBounds = boundsToMapboxFormat(bounds);
    if (!mapboxBounds) return;

    const defaultOptions = {
      padding: { top: 80, bottom: 80, left: 60, right: 60 },
      maxZoom: 17,
      duration: 1000,
      essential: true,
      ...options,
    };

    // Single point (or near-zero span): flyTo with a sensible zoom
    const lngSpan = Math.abs(mapboxBounds[1][0] - mapboxBounds[0][0]);
    const latSpan = Math.abs(mapboxBounds[1][1] - mapboxBounds[0][1]);
    if (lngSpan < 1e-6 && latSpan < 1e-6) {
      mapRef.current.flyTo({
        center: [mapboxBounds[0][0], mapboxBounds[0][1]],
        zoom: defaultOptions.maxZoom,
        duration: defaultOptions.duration,
        essential: true,
      });
      return;
    }

    mapRef.current.fitBounds(mapboxBounds, defaultOptions);
  } catch (error) {
    console.error('Error fitting map to bounds:', error);
  }
};

